import { isEmpty, isNotEmpty } from './dataUtils';
import {
  isArray,
  isDate,
  isFunction,
  isNotPresent,
  isNull,
  isObject,
  isPresent,
  isPrimitive,
  isUndefined,
} from './typeUtils';

export interface MergeOptions {
  overwriteWithEmpty: boolean;
  arrayMerge: <T>(a: T[], b: T[], options: MergeOptions) => T[];
}

export const clone = <T>(o: T, options: MergeOptions): T => {
  if (isNotPresent(o)) {
    return o;
  }
  if (isDate(o)) {
    return new Date(o.getTime()) as any as T;
  }
  if (isPrimitive(o)) {
    return o;
  }
  if (isArray(o)) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return mergeArray([], o, options) as any as T;
  }
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return mergeTwo({}, o, options);
};

export const mergeTwo = <T>(a: Partial<T>, b: Partial<T>, options: MergeOptions): T => {
  if (isUndefined(a)) {
    return clone(b, options) as T;
  }
  if (isUndefined(b)) {
    return clone(a, options) as T;
  }
  if (isNull(b)) {
    return options.overwriteWithEmpty ? null : (clone(a, options) as T);
  }
  if (isPrimitive(a) || isPrimitive(b) || isDate(a) || isDate(b)) {
    return clone(b, options) as T;
  }
  if (isArray(a) && isArray(b)) {
    return options.arrayMerge(a, b, options) as any as T;
  }
  if (isFunction(b)) {
    return b as any as T;
  }
  if (isFunction(a)) {
    return a as any as T;
  }

  const target: T = {} as T;
  Object.keys(a).forEach((key: string) => (target[key] = clone(a[key], options)));
  Object.keys(b).forEach((key: string) => {
    const val = b[key];
    if (isArray(val)) {
      target[key] = options.arrayMerge(target[key] || [], val, options);
    } else if (isObject(val)) {
      target[key] = mergeTwo(target[key] || {}, val, options);
    } else if (options.overwriteWithEmpty || isPresent(val)) {
      target[key] = clone(val, options);
    }
  });
  return target as T;
};

export const mergeArray = <T>(a: T[], b: T[], options: MergeOptions): T[] => {
  const c: T[] = a.slice();
  Object.keys(b).forEach((key: string) => {
    if (isObject(b[key])) {
      c[key] = mergeTwo(a[key] || {}, b[key], options);
    } else if (key in a) {
      c[key] = clone(b[key], options);
    } else {
      c.push(clone(b[key], options));
    }
  });
  return c;
};

export const overWriteArray = <T>(a: T[], b: T[], options: MergeOptions): T[] =>
  isNotEmpty(b) ? clone(b, options) : clone(a, options);

export const mergeDeep = <T>(objects: Partial<T>[], options: MergeOptions): T => {
  if (isEmpty(objects)) {
    return undefined;
  }
  return objects.reduce((acc: T, obj: Partial<T>) => mergeTwo(acc, obj, options), {}) as T;
};
